import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | error | success
  const [maskedEmail, setMaskedEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('No reset token found. Please use the link from your password reset email.');
      setStatus('error');
      return;
    }

    axios
      .get(`${API_BASE_URL}/auth/verify-reset-token`, { params: { token } })
      .then((res) => {
        if (res.data.success) {
          setMaskedEmail(res.data.data?.email || '');
          setStatus('valid');
        }
      })
      .catch((err) => {
        setTokenError(err.response?.data?.error || 'This password reset link is invalid or has expired.');
        setStatus('error');
      });
  }, [token]);

  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const pwError = validatePassword(password);
    if (pwError) {
      setFormError(pwError);
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/reset-password`, { token, password });
      if (res.data.success) {
        setStatus('success');
      }
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-grid container">
        <div className="login-hero d-none d-md-flex">
          <div className="login-hero-content">
            <div className="brand">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                className="border border-white rounded-3"
                alt="Zilch Logo"
                width={48}
                height={48}
              />
              <span>Zilch.</span>
            </div>
            <h2>Measure and manage your product carbon footprint.</h2>
            <p>Choose a new password and get straight back to your product library and PCF assessments.</p>
          </div>
          <div className="login-orb orb-1" />
          <div className="login-orb orb-2" />
          <div className="login-orb orb-3" />
        </div>

        <div className="login-card-wrapper">
          <div className="login-card">
            <div className="brand brand-center d-md-none mb-3">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                className="border border-white rounded-3"
                alt="Zilch Logo"
                width={40}
                height={40}
              />
              <span>Zilch.</span>
            </div>

            {status === 'loading' && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            )}

            {status === 'error' && (
              <>
                <h1 className="login-title">Link no longer valid</h1>
                <p className="login-subtitle">{tokenError}</p>
                <Link to="/forgot-password" className="login-button d-block text-center text-decoration-none mt-3">
                  Request a new link
                </Link>
                <div className="text-center mt-3">
                  <small className="text-white">
                    <Link to="/login" className="zilch-text-green">
                      Back to sign in
                    </Link>
                  </small>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <h1 className="login-title">Password updated</h1>
                <p className="login-subtitle">
                  Your password has been reset. Sign in with your new password to continue.
                </p>
                <Link to="/login" className="login-button d-block text-center text-decoration-none mt-3">
                  Sign in
                </Link>
              </>
            )}

            {status === 'valid' && (
              <>
                <h1 className="login-title">Set a new password</h1>
                <p className="login-subtitle">
                  {maskedEmail
                    ? <>Choose a new password for <strong>{maskedEmail}</strong>.</>
                    : 'Choose a new password for your account.'}
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      className="login-input"
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      autoFocus
                      required
                    />
                    <small className="login-subtitle d-block mt-1">
                      Must be 8+ characters with uppercase, lowercase, and number
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      className="login-input"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-button"
                    disabled={submitting || !password || !confirmPassword}
                  >
                    {submitting ? 'Updating…' : 'Reset password'}
                  </button>
                </form>

                {formError && (
                  <div className="login-error" role="alert">
                    {formError}
                  </div>
                )}

                <div className="text-center mt-3">
                  <small className="text-white">
                    <Link to="/login" className="zilch-text-green">
                      Back to sign in
                    </Link>
                  </small>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
